import React, { useState } from "react";
import Input from "./Input";
import Output from "./Output";
import SourceSelect from "../../shared/SourceSelect";
import { InputAction } from "../../../store/actions/InputAction";
import { connect } from "react-redux";
import { STRING_FIELDS, appliesToAllTraces } from "../../shared/dimensionFields";
import { applySourceChoice } from "../../shared/orientationSources";
import { withDshapeSection } from "../utils/section";



/**
 * Plotting Component
 * Coordinates the workflow between Input and Output components
 * Manages trace dimensions, orientation, and Redux state
 */
const Plotting = (props) => {
  // Default input values for first trace
  let firstinput = {
    direction: 120,
    inclination: 0,
    height: 8,
    hw: 5,
    width: 6,
    widthspl: 6,
    startrd: 0,
    endrd: 80,
    radiusofcrown: 3,
    dir: 70,
    rd: 45,
    amt: 50,
    iscrown: false,
    heightfrom: "leftinvert",
    pointheight: 4,
    orientation: "leftinvert",
    orientationsource: "known",
  };

  let details = [];
  if (props.inputDetails && props.inputDetails.dshape) {
    details = props.inputDetails.dshape;
  } else {
    details = [firstinput];
  }

  // Initialize dimensions with orientation/inclination if missing (backward compatibility)
  const initializeDimensions = (dims) => {
    return dims.map(dim => {
      const next = { ...dim };
      if (!next.orientation) next.orientation = "leftinvert";
      if (!next.orientationsource) next.orientationsource = "known";
      if (next.inclination === undefined || next.inclination === null || next.inclination === "") {
        next.inclination = 0;
      }
      return next;
    });
  };

  const [dimensions, setDimensions] = useState(initializeDimensions(details));
  const [traceCount, setTraceCount] = useState(details.length - 1);
  const [currentTrace, setCurrentTrace] = useState(details.length - 1);
  const [isOutput, setIsOutput] = useState(false);
  const [isEditInput, setIsEditInput] = useState(false);
  const [isDebug, setDebug] = useState(false);
  const [label, setLabel] = useState("Point Height");
  // Entering the activity, and each new trace, picks a source first; "Edit
  // Input" from the output does not re-ask.
  const [isSourceSelect, setIsSourceSelect] = useState(true);

  /**
   * A source was chosen on the selection screen for the current trace.
   */
  const chooseSource = (source) => {
    const newdim = [...dimensions];
    newdim[currentTrace] = applySourceChoice(withDshapeSection(newdim[currentTrace]), source);
    setDimensions(newdim);
    setIsSourceSelect(false);
  };

  /**
   * Update dimension value for current trace
   */
  const updateDimension = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    let newdim = [...dimensions];

    // Handle direction wrapping (360 = 0)
    if (name === "direction") {
      value = e.target.value === 360 ? 0 : e.target.value;
    }
    if (name === "dir") {
      value = e.target.value === 360 ? 0 : e.target.value;
    }

    const stored =
      STRING_FIELDS.includes(name) || name === "inclination" ? value : Number(value);
    const targets = appliesToAllTraces(name, currentTrace)
      ? newdim.map((_, i) => i)
      : [currentTrace];
    // Written in place: Input fires several updates in one handler, each
    // from the same render's array, and only shared objects carry them all.
    targets.forEach((i) => {
      newdim[i][name] = stored;
    });
    if(name === "tunnelside" && e.target.value === "center") {
      newdim[currentTrace]["distancefromcenter"] = 0;
    }

    setDimensions(newdim);
  };

  /**
   * Show output view and save to Redux
   */
  const showOutput = () => {
    setIsOutput(true);
    setDebug(false);
    setIsEditInput(false);
    let newdim = [...dimensions];
    let save = { ...props.inputDetails };
    save["dshape"] = newdim;
    props.inputAction(save);
  };

  /**
   * Show input view
   */
  const showInput = () => {
    setIsOutput(false);
    setDebug(false);
    setIsEditInput(true);
  };

  /**
   * Add a new trace
   */
  const addTrace = () => {
    let init;
    if (details[traceCount + 1]) {
      init = { ...details[traceCount + 1] };
    } else {
      init = { ...details[0] };
    }

    // Ensure orientation field is present
    if (!init.orientation) {
      init.orientation = "leftinvert";
    }

    let temp = [...dimensions];
    temp.push(init);
    setDimensions(temp);
    let newtracecount = traceCount + 1;
    setTraceCount(newtracecount);
    setCurrentTrace(newtracecount);
    setIsOutput(false);
    setIsSourceSelect(true);
  };

  /**
   * Delete a trace
   */
  const deleteTrace = (key) => {
    let temp = [...dimensions];
    if (key > -1) {
      temp.splice(key, 1);
    }
    setDimensions(temp);
    let newtracecount = traceCount - 1;
    setTraceCount(newtracecount);
    setCurrentTrace(newtracecount);

    let save = { ...props.inputDetails };
    save["dshape"] = temp;
    props.inputAction(save);
  };

  // Reset Values button: zero every input field, collapse to a single trace,
  // and persist so the reset survives leaving the page.
  const resetValues = () => {
    const zeroed = {
      ...firstinput,
      direction: 0,
      inclination: 0,
      height: 0,
      hw: 0,
      width: 0,
      startrd: 0,
      endrd: 0,
      dir: 0,
      rd: 0,
      amt: 0,
    };
    const newdim = [zeroed];
    setDimensions(newdim);
    setTraceCount(0);
    setCurrentTrace(0);
    let save = { ...props.inputDetails };
    save["dshape"] = newdim;
    props.inputAction(save);
  };

  return isSourceSelect ? (
    <SourceSelect
      onChoose={chooseSource}
      showobjective={() => props.setstep("objective")}
    />
  ) : !isOutput ? (
    <Input
      label="Create Trace"
      dimensions={dimensions}
      setcurrenttrace={setCurrentTrace}
      tracecount={traceCount}
      currenttrace={currentTrace}
      updatedimension={updateDimension}
      showoutput={showOutput}
      resetvalues={resetValues}
      heightlabel={label}
      isEditInput={isEditInput}
      showobjective={() => props.setstep("objective")}
      onChangeSource={() => setIsSourceSelect(true)}
    />
  ) : (
    <Output
      showinput={showInput}
      showdebug={isDebug}
      setshowdebug={setDebug}
      dimensions={dimensions}
      addtrace={addTrace}
      tracecount={traceCount}
      deletetrace={deleteTrace}
      currenttrace={currentTrace}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    inputDetails: state.InputReducer.selectedData,
  };
};

const mapDispatchToProps = {
  inputAction: InputAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(Plotting);





